"use client";

import { ArrowRight, Leaf, MapPin, Recycle as Recycled, Timer } from 'lucide-react';
import { Recycle, Clock3 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhyChooseOurWebsiteDesign() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "Affordable for Small Businesses",
    description: "You do not need a huge budget to have a website that works. We build high-performance custom websites tailored to your brand identity and your business goals. ",
    Icon: Leaf
  }, {
    number: "02",
    title: "Strategic SEO",
    description: "Our data-driven SEO strategies improve your search rankings and drive qualified foot traffic. We focus on long-term growth through SEO foundation, so Google can understand your business.",
    Icon: MapPin
  }, {
    number: "03",
    title: "Conversion-Focused Website Design",
    description: "A beautiful website is not enough. Your site needs to answer the right questions and guide visitors toward action. We place calls, forms, buttons, service sections, and trust signals where visitors are most likely to act.",
    Icon: Recycle
  }, {
    number: "04",
    title: "Rapid Implementation",
    description: "We value your time. Our streamlined development process ensures your new website or SEO campaign is live and delivering results faster than industry standards. Best for new businesses that need a simple online presence.",
    Icon: Clock3
  }];
  return <section data-section-id="5265" id="why-choose-our-website-design" className="bg-background text-foreground relative overflow-hidden ![padding:50px_0px_50px_0px]" style={{
    backgroundImage: "radial-gradient(circle, hsl(200 70% 55% / 0.04) 1px, transparent 1px)",
    backgroundSize: "24px 24px"
  }}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={titleRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-mono text-xs tracking-[0.2em] text-primary uppercase mb-8">[ 03 / ASAHI ADVANTAGE ]</p>
              <h2 className="font-sans font-default font-bold leading-[0.95] tracking-tight text-foreground mb-8 !text-5xl">Why Small Businesses Choose Asahi Search</h2>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed max-w-md mb-10">We understand that small businesses need websites that are professional, affordable, and practical. You do not need a complicated website. You need a clear website that builds trust and helps customers contact you.</p>
              <Link to="/AboutAsahiPage" className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-primary hover:text-primary/80 transition-colors group">
                
                
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="flex flex-col">
              {features.map((feature, i) => {
              const {
                ref,
                isVisible
              } = useRevealOnScroll<HTMLDivElement>({
                threshold: 0.25
              });
              const Icon = feature.Icon;
              return <div key={i} data-index={i} ref={ref} className={`group relative py-10 md:py-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
                transitionDelay: `${i * 80}ms`
              }}>
                    {}
                    <div className={`absolute top-0 left-0 h-px bg-primary/70 transition-all duration-1000 ease-out group-hover:bg-primary group-hover:shadow-[0_0_12px_hsl(200_70%_55%/0.6)] ${isVisible ? "w-full" : "w-0"}`} style={{
                  transitionDelay: `${i * 80 + 100}ms`
                }} />

                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-baseline gap-6 mb-5">
                          <span className="font-mono text-4xl md:text-5xl lg:text-[64px] font-bold text-muted-foreground/70 leading-none tracking-tight transition-transform duration-500 group-hover:-translate-y-1 group-hover:text-primary">
                            {feature.number}
                          </span>
                          <h3 className="font-sans font-default font-bold text-2xl md:text-3xl lg:text-[36px] leading-tight tracking-tight text-foreground">
                            {feature.title}
                          </h3>
                        </div>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl pl-0 md:pl-[88px] lg:pl-[104px]">
                          {feature.description}
                        </p>
                      </div>

                      <div className="flex-shrink-0 pt-2">
                        <div className="h-12 w-12 md:h-14 md:w-14 rounded-lg border border-border/60 flex items-center justify-center transition-all duration-500 group-hover:rotate-[8deg] group-hover:border-primary/60">
                          <Icon className="h-5 w-5 md:h-6 md:w-6 text-foreground/70 group-hover:text-primary transition-colors duration-500" strokeWidth={1} />
                        </div>
                      </div>
                    </div>
                  </div>;
            })}

              {}
              <div className="h-px bg-border/40 w-full" />
            </div>

            {}
            <div className="mt-12 lg:hidden">
              <Button asChild variant="outline" className="font-mono text-xs tracking-[0.2em] uppercase border-primary/40 text-primary hover:bg-primary/10 hover:text-primary rounded-lg">
                <Link to="/AboutAsahiPage">
                  Our Agency Story
                  <ArrowRight className="h-3.5 w-3.5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}